import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ArrowLeft, Save, Sparkles, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { GeneratedContent } from "@/types/content";
import { useAuth } from "@/contexts/AuthContext";
import { TwitterPreview } from "@/components/content/previews/TwitterPreview";
import { LinkedInPreview } from "@/components/content/previews/LinkedInPreview";
import { BlogPreview } from "@/components/content/previews/BlogPreview";
import { VideoPreview } from "@/components/content/previews/VideoPreview";
import { PodcastPreview } from "@/components/content/previews/PodcastPreview";
import { ContentEditor } from "@/components/content/ContentEditor";

const ContentEditorPage = () => {
  const { contentId, projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [reviewedContent, setReviewedContent] = useState("");
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const { data: contentData, isLoading, error } = useQuery({
    queryKey: ['content', contentId],
    queryFn: async () => {
      if (!contentId || !user?.id) throw new Error('Content ID and user ID are required');
      
      console.log('Fetching content with ID:', contentId);
      
      const { data, error } = await supabase
        .from('generated_content')
        .select()
        .eq('id', contentId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching content:', error);
        throw error;
      }
      if (!data) {
        throw new Error('Content not found');
      }
      
      console.log('Fetched content:', data);
      return data as GeneratedContent;
    },
    enabled: !!contentId && !!user?.id
  });

  // Set initial content when data is loaded
  useEffect(() => {
    if (contentData) {
      setContent(contentData.content || '');
      setTitle(contentData.title || '');
      // If there's reviewed content in metadata, use it
      if (contentData.metadata?.reviewedContent) {
        setReviewedContent(contentData.metadata.reviewedContent);
      } else {
        setReviewedContent('');
      }
    }
  }, [contentData]);

  useEffect(() => {
    if (contentData) {
      const originalContentChanged = content !== contentData.content;
      const titleChanged = title !== contentData.title;
      const reviewedContentChanged = 
        reviewedContent !== (contentData.metadata?.reviewedContent || '');
      
      setHasUnsavedChanges(
        originalContentChanged || 
        titleChanged || 
        reviewedContentChanged
      );
    }
  }, [content, title, reviewedContent, contentData]);

  const handleBack = useCallback(() => {
    if (!projectId) {
      // If projectId is not available from params, try to get it from contentData
      if (contentData?.project_id) {
        if (hasUnsavedChanges) {
          setShowUnsavedDialog(true);
        } else {
          navigate(`/projects/${contentData.project_id}/content`);
        }
      } else {
        // If we still don't have a project ID, navigate to projects list
        navigate('/projects');
      }
    } else {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
      } else {
        navigate(`/projects/${projectId}/content`);
      }
    }
  }, [hasUnsavedChanges, navigate, projectId, contentData]);

  const handleSave = async () => {
    if (!contentId || !user?.id) return;

    setIsSaving(true);
    try {
      // Prepare metadata with reviewedContent
      const updatedMetadata = {
        ...(contentData?.metadata || {}),
        reviewedContent: reviewedContent
      };

      const { error } = await supabase
        .from('generated_content')
        .update({ 
          content,
          title,
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', contentId)
        .eq('user_id', user.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['content', contentId] });
      queryClient.invalidateQueries({ queryKey: ['generated-content'] });

      toast({
        title: "Success",
        description: "Content updated successfully",
      });

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "Error",
        description: "Failed to update content",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasUnsavedChanges(true);
  };

  const handleReviewedContentChange = (newContent: string) => {
    setReviewedContent(newContent);
    setHasUnsavedChanges(true);
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              Error loading content: {error instanceof Error ? error.message : String(error)}
            </div>
            <Button onClick={() => navigate(`/projects/${projectId}/content`)} className="mt-4">
              Back to Content
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-4xl mx-auto animate-pulse">
          <CardHeader className="h-20 bg-muted" />
          <CardContent className="space-y-4">
            <div className="h-10 bg-muted rounded" />
            <div className="h-96 bg-muted rounded" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-background to-background/80">
        <div className="container mx-auto px-2 py-2">
          <Card className="border-blue-500/20 h-[calc(100vh-24px)]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -z-10" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -z-10" />
            
            <CardHeader className="flex flex-row items-center justify-between border-b py-3 px-4">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={handleBack}
                  className="hover:bg-blue-500/5"
                  size="sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-blue-500 to-blue-500 bg-clip-text text-transparent">
                    {contentData?.platform ? (
                      <span className="capitalize">{contentData.platform}</span>
                    ) : 'Content'} Editor
                  </h1>
                  
                  {contentData && (
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" 
                        className="capitalize bg-blue-500/10 text-blue-500 border-blue-500/20 text-xs py-0">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {contentData.platform}
                      </Badge>
                      {contentData.metadata?.audience && (
                        <Badge variant="outline" 
                          className="capitalize bg-blue-500/10 text-blue-500 border-blue-500/20 text-xs py-0">
                          {contentData.metadata.audience}
                        </Badge>
                      )}
                      <Badge variant="outline" 
                        className="capitalize bg-green-500/10 text-green-500 border-green-500/20 text-xs py-0">
                        <Clock className="w-3 h-3 mr-1" />
                        {contentData.created_at ? format(new Date(contentData.created_at), 'MMM d, yyyy') : 'No date'}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/projects/${projectId}/content`)}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={isSaving || !hasUnsavedChanges}
                  className={cn(
                    "bg-gradient-to-r from-blue-500 to-blue-500",
                    "hover:from-blue-600 hover:to-blue-600",
                    "disabled:from-gray-400 disabled:to-gray-500"
                  )}
                  size="sm"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-3 flex flex-col h-[calc(100%_-_64px)]">
              <div className="space-y-3 flex flex-col h-full">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Title</label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="text-lg font-medium border-blue-500/20 focus-visible:ring-blue-500/20"
                    placeholder="Enter title..."
                  />
                </div>

                {contentData && (
                  <div className="flex-1 overflow-hidden">
                    <ContentEditor 
                      content={{
                        ...contentData,
                        content: content,
                        title: title
                      }}
                      onContentChange={handleContentChange}
                      reviewedContent={reviewedContent || undefined}
                      onReviewedContentChange={handleReviewedContentChange}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent>
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-semibold">Unsaved Changes</h3>
            <p className="text-muted-foreground">
              You have unsaved changes. Are you sure you want to leave?
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowUnsavedDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setShowUnsavedDialog(false);
                  if (projectId) {
                    navigate(`/projects/${projectId}/content`);
                  } else if (contentData?.project_id) {
                    navigate(`/projects/${contentData.project_id}/content`);
                  } else {
                    navigate('/projects');
                  }
                }}
              >
                Leave Without Saving
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentEditorPage;
